import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostListener, OnInit} from '@angular/core';
import {AuthService} from '../../_services/auth.service';
import {MatDialog} from '@angular/material/dialog';
import {SettingsComponent} from '../settings/settings.component';
import {LevelsComponent} from '../levels/levels.component';
import {BadgesComponent} from '../badges/badges.component';
import {ActivatedRoute, Router} from '@angular/router';
import {User, UserWithModerationInfo} from '../../_types/user';
import {EMPTY, filter, Observable, startWith, switchMap, tap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Community, CommunityPage, SubGroup, UserAccessLevel} from '../../_types/communities';
import {CommunitiesService} from '../../_services/communities.service';
import {ReportsService} from '../../_services/reports.service';
import {createPeriodicCalls} from '../../_utils/http-params';
import {SnackbarService} from '../../_services/snackbar.service';
import {ThemeService} from "../../_services/theme.service";
import {UserInfoComponent} from "../user-info/user-info.component";
import {PointsLeaderboardComponent} from "../../feed/points-leaderboard/points-leaderboard.component";
import {RecentActivityComponent} from "../recent-activity/recent-activity.component";
import {LocalStorageService} from "../../_services/local-storage.service";
import {animate, group, query, style, transition, trigger} from "@angular/animations";
import {mostReadable, TinyColor} from "@ctrl/tinycolor";

enum currentBrightnessTheme {
    light = "light",
    dark = "dark",
    default = "default"
}


@Component({
    selector: 'app-authview-header',
    templateUrl: './authview-header.component.html',
    styleUrl: './authview-header.component.scss',
    animations: [
        trigger('openClose', [
            transition('closed => open', group([
                query('rect:nth-child(1)', [
                    animate('300ms ease-out', style({
                        transform: 'translateY(32.5px) translateX(-25px) rotate(45deg)',
                        transformOrigin: '50% 50%'
                    }))
                ]),
                query('rect:nth-child(2)', [
                    animate('150ms ease-out', style({
                        opacity: 0
                    }))
                ]),
                query('rect:nth-child(3)', [
                    animate('300ms ease-out', style({
                        transform: 'translateY(-12.5px) translateX(-22.5px) rotate(-45deg)',
                        transformOrigin: '50% 50%'
                    }))
                ])
            ])),
            transition('open => closed', group([
                query('rect:nth-child(1)', [
                    animate('300ms ease-out', style({
                        transform: 'translateY(0) translateX(0) rotate(0)',
                        transformOrigin: '50% 50%'
                    }))
                ]),
                query('rect:nth-child(2)', [
                    animate('150ms ease-out', style({
                        opacity: 1
                    }))
                ]),
                query('rect:nth-child(3)', [
                    animate('300ms ease-out', style({
                        transform: 'translateY(0) translateX(0) rotate(0)',
                        transformOrigin: '50% 50%'
                    }))
                ])
            ])),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class AuthviewHeaderComponent implements OnInit {
    companyImg: string = localStorage.getItem('communityLogo') || 'assets/explainLogoPlaceholder.png';
    community: Community | null = null;
    subgroups: SubGroup[] = [];
    subgroupsMenuList: SubGroup[] = [];
    allSubgroups: SubGroup[] = [];
    currentUser: User | null = null;
    isSuperuser: boolean = false;
    isDarkMode: boolean = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    isMobile: boolean = false;
    isApp: boolean = false;
    menuOpen: boolean = false;
    listItems: CommunityPage[] = []

    constructor(
        private authService: AuthService,
        private communitiesService: CommunitiesService,
        private snackbarService: SnackbarService,
        public dialog: MatDialog,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly reportService: ReportsService,
        private readonly destroyRef: DestroyRef,
        private themeService: ThemeService,
        private readonly localStorageService: LocalStorageService,
        private readonly cdr: ChangeDetectorRef,
    ) {

        this.isApp = this.localStorageService.getItem('isApp', false);
        this.themeService.currentBrightnessTheme.subscribe((value: currentBrightnessTheme): void => {
            this.isDarkMode = (value == currentBrightnessTheme.dark)
        });

        this.currentUser = this.authService.getUser();
        if (!this.currentUser) {
            this.authService.refreshUser().then((res: User | null): void => {
                this.currentUser = res;
                this.cdr.detectChanges();
            });
        }
        this.isSuperuser = this.authService.isSuperUser();
        this.communitiesService.getCurrentDashboardCommunity()
            .pipe(
                switchMap((community): Observable<SubGroup[]> => {
                    this.community = community;
                    if (this.community) {
                        if (this.community.logo) {
                            localStorage.setItem('communityLogo', this.community.logo)
                        } else {
                            localStorage.removeItem('communityLogo')
                        }
                        if (this.community.name) {

                            localStorage.setItem('communityName', this.community.name)
                        } else {
                            localStorage.removeItem('communityName')
                        }
                        if (this.community.background_image) {

                            localStorage.setItem('communityLoginBG', this.community.background_image)
                        } else {
                            localStorage.removeItem('communityLoginBG')
                        }
                        if (this.community.main_background_image) {
                            this.communitiesService.changeBackgroundImage(this.community.main_background_image)
                        } else {
                            this.communitiesService.changeBackgroundImage('')
                        }
                    }
                    if (community?.id) {
                        this.listItems = []
                        this.communitiesService.getCommunityPages(community?.id).subscribe(r => {
                            for (let item of r) {
                                if (item.display_in_dropdown) {
                                    this.listItems.push(item)
                                }
                            }
                            this.listItems?.sort((a, b) => a.order - b.order);
                        })
                    }

                    if (community?.dashboard_navbar_logo) {
                        this.companyImg = community?.dashboard_navbar_logo;
                    } else if (community?.logo) {
                        this.companyImg = community?.logo;
                    }

                    return community?.id
                        ? this.getSubgroups(community.id)
                        : EMPTY;
                }),
                switchMap((): Observable<UserWithModerationInfo | null> => {
                    return this.communitiesService.getUserInformationForCommunity(this.community!.id).pipe(
                        tap((res): void => {
                            this.communitiesService.emailVerified = !res ? true : (res?.email_verified === null || !!res?.email_verified);
                            this.communitiesService.profileRefreshed = !res ? true : (res?.profile_refreshed === null || !!res?.profile_refreshed);
                            if (!this.communitiesService.emailVerified) {
                                this.router.navigate(['/verify']).then();
                                return;
                            }
                            if (!this.communitiesService.profileRefreshed && this.community?.questions?.length) {
                                this.router.navigate(['/profile-questions']).then();
                                return;
                            }
                            if (res?.user?.requires_sso_setup) {
                                localStorage.setItem('SSOUser', JSON.stringify({user: res.user}));
                                localStorage.removeItem('currentUser')
                                this.router.navigate(['/registrationStep', res.user?.email]);
                                return;
                            }
                        })
                    )
                }),
                takeUntilDestroyed()
            )
            .subscribe();
        this.checkReportStatus();
    }

    get isDashboardPage(): boolean {
        return this.router.url.toLowerCase().includes('/dashboard');
    }

    get currentUserName(): string {
        const fName: string = this.currentUser?.first_name || '';
        const lName: string = this.currentUser?.last_name || '';
        const fullName: string = `${fName} ${lName}`.trim();
        return fullName || this.currentUser?.username || '';
    }

    get waitingReportId(): string | null {
        try {
            return JSON.parse(sessionStorage?.getItem('awaitReport') ?? 'null');
        } catch (error) {
            return null;
        }
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.communitiesService.userHasCommunityRole(roles);
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any): void {
        this.isMobile = window.innerWidth <= 1000;
    }

    ngOnInit(): void {
        this.isMobile = window.innerWidth <= 1000;
    }

    isMainFeed: () => boolean = (): boolean => {
        return this.router.url.includes('/feed') && !this.route.snapshot.queryParamMap.has('subgroup');
    }

    isSubgroupFeed: (id: number) => boolean = (id: number): boolean => {
        return this.router.url.includes('/feed') && this.route.snapshot.queryParamMap.get('subgroup') === id.toString();
    }

    isSubgroupsFeed: (subgroups: SubGroup[]) => boolean = (subgroups: SubGroup[]): boolean => {
        let includes = false;
        for (const subgroup of subgroups) {
            if (this.router.url.includes('/feed') && this.route.snapshot.queryParamMap.get('subgroup') === subgroup.id.toString()) {
                includes = true;
            }
        }
        return includes
    }

    logout(): void {
        this.authService.signOut();
    }

    settings(): void {
        this.dialog.open(SettingsComponent, {
            panelClass: ['mediumWindow', 'settingsWindow'],
            autoFocus: false
        });
    }

    openProfile(): void {
        this.dialog.open(UserInfoComponent, {
            panelClass: ['mediumWindow', 'fakeDialog'],
            autoFocus: false,
            data: {
                user: this.currentUser,
                largeAvatar: true
            }
        });
    }

    openRecentActivity(): void {
        this.dialog.open(RecentActivityComponent, {
            panelClass: ['mediumWindow', 'fakeDialog'],
            autoFocus: false,
            data: {
                community: this.community,
            }
        });
    }

    openPointsLeaderboard(): void {
        this.dialog.open(PointsLeaderboardComponent, {
            panelClass: ['mediumWindow', 'fakeDialog'],
            autoFocus: false,
            data: {
                isDialog: true
            }
        });
    }

    openLevels(): void {
        this.dialog.open(LevelsComponent, {
            panelClass: 'mediumWindow',
            data: {user: this.currentUser, community: this.community}
        });
    }

    openBadges(): void {
        this.dialog.open(BadgesComponent, {panelClass: 'mediumWindow', maxHeight: '80vh'});
    }

    onAnimationDone(menuOpen: boolean): void {
        this.menuOpen = menuOpen;
    }

    getContrastColor(color?: string): string {
        if (color) {
            let fallback: string = new TinyColor(color).isLight() ? 'rgba(black, 0.87)' : 'white';
            let mostReadableColor = mostReadable(color, ["#ffffff", "#000000", "#808080", "#C0C0C0"], {includeFallbackColors: true})?.toHexString()
            if (!mostReadableColor) {
                mostReadableColor = fallback;
            }
            return mostReadableColor
        } else {
            return "#000000"
        }
    }

    isOnSubgroupPage(subgroupId: number): boolean {
        let isSubgroup: boolean = false
        this.route.queryParams.subscribe(params => {
            if (Number(params['subgroup']) == subgroupId) {
                isSubgroup = true;
            }
        });
        return isSubgroup;
    }

    private getSubgroups(id: number): Observable<SubGroup[]> {
        this.subgroups = [];
        this.subgroupsMenuList = [];
        return this.communitiesService.getSubGroups(id)
            .pipe(
                tap(res => {
                    this.allSubgroups = res || [];
                    if (res.length <= 3) {
                        this.subgroups = res;
                    } else {
                        this.subgroups = res.slice(0, 2)
                        this.subgroupsMenuList = res.slice(2);
                    }
                    if (this.community && !this.community?.hide_main_feed_in_navbar) {
                        this.subgroups.unshift({
                            id: 0,
                            button_color_hex: this.community?.main_feed_button_color_hex || this.community?.dashboard_button_color_hex || ((this.community?.dark_header || this.isDarkMode) ? 'white' : 'black'),
                            main_feed: true,
                            order: this.community.main_feed_order,
                            description: 'All Posts',
                            name: this.community?.main_feed_name ? this.community?.main_feed_name : 'Main Feed',
                        })
                        this.subgroups = this.subgroups?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                    }
                })
            );
    }

    private checkReportStatus(): void {
        this.reportService.reportStatusListener()
            .pipe(
                startWith(this.waitingReportId),
                filter((): boolean => !!this.waitingReportId),
                switchMap(() => {
                    return createPeriodicCalls(
                        this.reportService.checkReportStatus(this.waitingReportId as string),
                        this.reportService.reportStatusListener().pipe(takeUntilDestroyed(this.destroyRef))
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (res): void => {
                    if (res.data.status === 'SUCCESS') {
                        sessionStorage.removeItem('awaitReport');
                        this.snackbarService.openSuccessSnackBar(`Your report has been generated successfully`);
                    } else if (['FAILURE', 'REVOKED'].includes(res.data.status)) {
                        const id: string | null = this.waitingReportId;
                        sessionStorage.removeItem('awaitReport');
                        this.snackbarService.openErrorSnackBar(`There was an error creating your report [id: ${id}]`);
                    }
                },
                error: (err): void => {
                    console.error(err);
                    const id: string | null = this.waitingReportId;
                    sessionStorage.removeItem('awaitReport');
                    if (err == 'Report not found or expired') {
                        // If the report generated too quick.
                        this.snackbarService.openSuccessSnackBar(`Your report has been generated successfully`);
                    } else {
                        this.snackbarService.openErrorSnackBar(`There was an error creating your report [id: ${id}]`);
                    }
                }
            })
    }

    goToHomeUrl(): void {
        this.router.navigateByUrl(this.community?.home_page_path || '/dashboard');
    }
}
